// include fullcalendar css
// currently in app.tsx, but should be in components
// using fullcalendar when nextjs updated to v10
// see https://nextjs.org/blog/next-10#importing-css-from-third-party-react-components
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/list/main.css';
import '@fullcalendar/timegrid/main.css';
import '@formatjs/intl-locale/polyfill';
import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import { GetServerSidePropsContext, Redirect } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import 'normalize.css';
import NProgress from 'nprogress';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import ReactModal from 'react-modal';
import svgSprite from '../../public/static/sprite.svg';

import * as LocaleContext from '../contexts/LocaleContext';
import * as ViewDeviceContext from '../contexts/ViewDeviceContext';
import store from '../redux';

import '../styles/global.css';

import I18n, { detectLocaleByAcceptLanguage } from '../utils/i18n';
import {
  setCoubicLocaleRequestHeader,
  setUaType,
} from '../utils/secured-axios';
import './_app.scss';
import nookies from 'nookies';
import Script from 'next/script';
import { userMeSwrKey, useUserMeFetcher, useUserMe } from '../hooks/api/me';
import { preload } from 'swr';
import { checkIsCoubicApp } from '../utils/mobileApp';
import { initializeDayjs } from 'src/libs/dayjs/initialize-dayjs';
import { SupervisorProxyHeader } from 'src/molecules/SupervisorProxyHeader';
import { MulpayProvider } from 'src/components/gmo-pg/MulpayProvider';
import {
  getRequestUrl,
  reactElementWithRequestUrl,
  withRequestUrlFromServerSidePropsContext,
} from 'src/utils/request-url';
import { ServerSidePropsLogger } from 'src/lib/logger/ssr-http-logging';
import { RSDialog } from 'src/reservation-page/modality/RSDialog';

initializeDayjs();

// _app の実装的に props: Promise<Props> をハナからサポートできていなかったので、型で嘘をつかないように再定義した
export type GetServerSidePropsResult<Props> =
  | { props: Props }
  | { redirect: Redirect }
  | { notFound: true };

const useLocale = () => {
  const router = useRouter();
  const { hl } = nookies.get();
  const { meResponseData, error } = useUserMe();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!error) return;
    if (!axios.isAxiosError(error)) return;
    if (error.response?.status !== 400) return;

    const errMsg = error.response?.data?.message;
    // ドメイン移行作業中に予約者アプリからアクセスすると、タイミング次第でこのエラーが発生する可能性がある。
    // アプリを再起動するとリカバリできるので、その旨を通知。
    // このコードは、ドメイン移行作業が完了した後は削除してよい。
    if (errMsg === 'organization_inconsistent_error_on_end_user_app') {
      setErrorMessage(
        'アプリの再起動が必要です。お手数ですが、アプリを終了させてから再度開いてください。',
      );
      setErrorDialogOpen(true);
    }
  }, [error]);

  const locale = (router.query.hl as string) || meResponseData?.locale || hl;
  if (I18n.isAvailableCoubicLocale(locale)) {
    setCoubicLocaleRequestHeader(locale);
  }
  return { locale, errorDialogOpen, setErrorDialogOpen, errorMessage };
};

const setLocale = (ctx: GetServerSidePropsContext) => {
  const { hl } = nookies.get(ctx);
  const locale = (ctx.query.hl ||
    hl ||
    (ctx.req &&
      detectLocaleByAcceptLanguage(ctx.req.headers['accept-language'])) ||
    '') as string;
  if (I18n.isAvailableCoubicLocale(locale)) {
    setCoubicLocaleRequestHeader(locale);
  }
};

export const injectAppContextGetServerSideProps =
  <P,>(
    innerGetServerSideProps: (
      ctx: AppGetServerSidePropsContext,
    ) => Promise<GetServerSidePropsResult<P>>,
  ) =>
  async (
    ctx: GetServerSidePropsContext & { requestId?: string },
  ): Promise<GetServerSidePropsResult<P & { requestUrl: string }>> =>
    withRequestUrlFromServerSidePropsContext(ctx, async () => {
      // SSR本体のリクエストとSSR内のAPIリクエストを関連づけるために生成する
      // 本来はSSRの前段のLB等で生成して渡すべきだが現状ヘッダにそのような値は渡ってこないためここで生成する
      const requestID = ctx.requestId || crypto.randomUUID().toString();
      const ssrLogger = new ServerSidePropsLogger(requestID);
      ssrLogger.logAccess(ctx.req);

      try {
        setUaType(ctx.req.headers['user-agent'] ?? '');
        setLocale(ctx);
        const { cb, token, uatype, user_agent } = nookies.get(ctx);
        const isCoubicApp = checkIsCoubicApp(uatype, user_agent);
        const result = await innerGetServerSideProps({
          ...ctx,
          requestId: requestID,
          session: cb ?? '',
          token: token ?? '',
          isCoubicApp,
        });
        if ('props' in result) {
          result.props = { ...result.props, requestUrl: getRequestUrl() };
        }
        return JSON.parse(JSON.stringify(result));
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          err.response &&
          err.response.status === 404
        ) {
          ssrLogger.logError(err, 'SSR request returned 404', 'warn');
          return { notFound: true };
        }
        ssrLogger.logError(err);
        throw err;
      }
    });

export interface AppGetServerSidePropsContext extends GetServerSidePropsContext {
  session: string;
  token: string;
  requestId: string;
  isCoubicApp: boolean;
}

// ウォーターフォールを防ぐ
// https://swr.vercel.app/ja/docs/prefetching
typeof window !== 'undefined' && preload(userMeSwrKey, useUserMeFetcher);

const CbcApp = ({
  Component,
  pageProps: { requestUrl, ...pageProps },
}: AppProps) => {
  const router = useRouter();

  useEffect(() => {
    // react-modal a11y setup (required for correct aria-hide behavior)
    ReactModal.setAppElement('#__next');

    // NProgress
    router.events.on('routeChangeStart', () => NProgress.start());
    router.events.on('routeChangeComplete', () => NProgress.done());
    router.events.on('routeChangeError', () => NProgress.done());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { locale, errorDialogOpen, setErrorDialogOpen, errorMessage } =
    useLocale();

  return reactElementWithRequestUrl(requestUrl, () => (
    <>
      <Script src="https://js.stripe.com/v3/" strategy="beforeInteractive" />
      <Script src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      />
      <Sentry.ErrorBoundary>
        <ViewDeviceContext.ViewDeviceContextProvider>
          <LocaleContext.LocaleContextProvider locale={locale}>
            <MulpayProvider>
              <Provider store={store}>
                <SupervisorProxyHeader />
                <Component {...pageProps} />
              </Provider>
            </MulpayProvider>
          </LocaleContext.LocaleContextProvider>
        </ViewDeviceContext.ViewDeviceContextProvider>
        {/* FIXME: inner html => SVGR を使う方法に改善する */}
        <div
          dangerouslySetInnerHTML={{ __html: svgSprite }}
          style={{ display: 'none' }}
        />
      </Sentry.ErrorBoundary>
      <RSDialog
        type="onlyAction"
        isOpen={errorDialogOpen}
        onRequireClosing={() => setErrorDialogOpen(false)}
        title="エラー"
        description={errorMessage}
        actionText="OK"
        onRequireAction={() => setErrorDialogOpen(false)}
      />
    </>
  ));
};

export default CbcApp;
